/**
 * HTML/CSS HUD overlay for gameplay (M24 - polished layout).
 * Layout:
 *   Top-left: Class icon + Floor + Room count
 *   Top-center: Boss health bar (when applicable)
 *   Top-right: Crypt tokens (minimap is PixiJS overlay)
 *   Bottom-left: HP bar + XP bar + Level + Status effects + Ability [Q]
 *   Bottom-center: Quick slot inventory bar (1-5)
 *   Bottom-right: Weapon name
 */
export class HUD {
  constructor(uiRoot) {
    this.uiRoot = uiRoot;
    this.elements = {};
    this._create();
  }

  _create() {
    // === TOP-LEFT: Class + Floor + Rooms ===
    const topLeft = document.createElement('div');
    topLeft.id = 'hud-top-left';
    topLeft.style.cssText = `
      position: absolute;
      top: 4px; left: 4px;
      background: rgba(0, 0, 0, 0.55);
      border: 1px solid rgba(255, 255, 255, 0.08);
      padding: 3px 6px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      font-size: 10px;
      color: #cccccc;
      display: flex;
      align-items: center;
      gap: 6px;
    `;

    // Class icon (colored square placeholder)
    const classIcon = document.createElement('span');
    classIcon.style.cssText = `
      display: inline-block;
      width: 10px; height: 10px;
      background: #4488cc;
      flex-shrink: 0;
    `;
    this.elements.classIcon = classIcon;

    const floorRoomCol = document.createElement('div');
    floorRoomCol.style.cssText = `display: flex; flex-direction: column; gap: 1px;`;

    const floorLabel = document.createElement('div');
    floorLabel.style.cssText = `font-size: 10px; font-weight: bold; color: #cccccc;`;
    floorLabel.textContent = 'Floor 1';
    this.elements.floorLabel = floorLabel;

    const roomLabel = document.createElement('div');
    roomLabel.style.cssText = `font-size: 8px; color: #888888;`;
    roomLabel.textContent = 'Rooms: 0/0';
    this.elements.roomLabel = roomLabel;

    floorRoomCol.appendChild(floorLabel);
    floorRoomCol.appendChild(roomLabel);
    topLeft.appendChild(classIcon);
    topLeft.appendChild(floorRoomCol);
    this.elements.topLeft = topLeft;

    // === TOP-RIGHT: Crypt Tokens ===
    const topRight = document.createElement('div');
    topRight.id = 'hud-top-right';
    topRight.style.cssText = `
      position: absolute;
      top: 4px; right: 108px;
      background: rgba(0, 0, 0, 0.55);
      border: 1px solid rgba(140, 100, 220, 0.2);
      padding: 3px 6px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      font-size: 9px;
      color: #bb88ff;
      display: flex;
      align-items: center;
      gap: 4px;
    `;

    const tokenIcon = document.createElement('span');
    tokenIcon.style.cssText = `
      display: inline-block;
      width: 7px; height: 7px;
      background: #9955dd;
      transform: rotate(45deg);
      flex-shrink: 0;
    `;

    const tokenText = document.createElement('span');
    tokenText.textContent = '0';
    this.elements.tokenText = tokenText;

    topRight.appendChild(tokenIcon);
    topRight.appendChild(tokenText);
    this.elements.topRight = topRight;

    // === TOP-CENTER: Boss Health Bar (hidden by default) ===
    const bossBarWrapper = document.createElement('div');
    bossBarWrapper.id = 'boss-health-bar';
    bossBarWrapper.style.cssText = `
      position: absolute;
      top: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 180px;
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 1px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      z-index: 50;
    `;

    const bossNameLabel = document.createElement('div');
    bossNameLabel.style.cssText = `
      font-size: 10px;
      font-weight: bold;
      color: #ff8844;
      text-shadow: 0 0 4px rgba(255, 100, 0, 0.5);
    `;
    this.elements.bossNameLabel = bossNameLabel;

    const bossHpBarBg = document.createElement('div');
    bossHpBarBg.style.cssText = `
      width: 180px; height: 6px;
      background: rgba(30, 30, 30, 0.8);
      border: 1px solid rgba(255, 100, 0, 0.3);
    `;

    const bossHpBarFill = document.createElement('div');
    bossHpBarFill.style.cssText = `
      width: 100%; height: 100%;
      background: #cc4422;
      transition: width 0.3s ease-out;
    `;
    this.elements.bossHpBarFill = bossHpBarFill;

    const bossHpPctLabel = document.createElement('div');
    bossHpPctLabel.style.cssText = `font-size: 8px; color: #cc8866;`;
    this.elements.bossHpPctLabel = bossHpPctLabel;

    bossHpBarBg.appendChild(bossHpBarFill);
    bossBarWrapper.appendChild(bossNameLabel);
    bossBarWrapper.appendChild(bossHpBarBg);
    bossBarWrapper.appendChild(bossHpPctLabel);
    this.elements.bossBarWrapper = bossBarWrapper;

    // === BOTTOM-LEFT: HP, XP, Level, Status, Ability ===
    const bottomLeft = document.createElement('div');
    bottomLeft.id = 'hud-bottom-left';
    bottomLeft.style.cssText = `
      position: absolute;
      bottom: 4px; left: 4px;
      background: rgba(0, 0, 0, 0.55);
      border: 1px solid rgba(255, 255, 255, 0.08);
      padding: 4px 6px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 110px;
    `;

    // HP label + bar
    const hpRow = document.createElement('div');
    hpRow.style.cssText = `display: flex; align-items: center; gap: 4px;`;

    const hpLabel = document.createElement('div');
    hpLabel.style.cssText = `font-size: 9px; color: #cc3333; min-width: 20px;`;
    hpLabel.textContent = 'HP';
    this.elements.hpLabel = hpLabel;

    const hpBarBg = document.createElement('div');
    hpBarBg.style.cssText = `
      width: 80px; height: 6px;
      background: rgba(50, 50, 50, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.06);
    `;

    const hpBarFill = document.createElement('div');
    hpBarFill.style.cssText = `
      width: 100%; height: 100%;
      background: #cc3333;
      transition: width 0.3s ease-out;
    `;
    this.elements.hpBarFill = hpBarFill;

    const hpText = document.createElement('div');
    hpText.style.cssText = `font-size: 8px; color: #aaaaaa; min-width: 40px; text-align: right;`;
    hpText.textContent = '100/100';
    this.elements.hpText = hpText;

    hpBarBg.appendChild(hpBarFill);
    hpRow.appendChild(hpLabel);
    hpRow.appendChild(hpBarBg);
    hpRow.appendChild(hpText);
    bottomLeft.appendChild(hpRow);

    // Shield bar (Voidpriest)
    const shieldBarBg = document.createElement('div');
    shieldBarBg.style.cssText = `
      width: 80px; height: 3px;
      background: rgba(50, 50, 50, 0.6);
      margin-left: 24px;
      display: none;
    `;
    const shieldBarFill = document.createElement('div');
    shieldBarFill.style.cssText = `
      width: 0%; height: 100%;
      background: #ffcc44;
      transition: width 0.2s ease-out;
    `;
    this.elements.shieldBarBg = shieldBarBg;
    this.elements.shieldBarFill = shieldBarFill;
    shieldBarBg.appendChild(shieldBarFill);
    bottomLeft.appendChild(shieldBarBg);

    // XP bar (M22)
    const xpRow = document.createElement('div');
    xpRow.style.cssText = `display: flex; align-items: center; gap: 4px;`;

    const xpLabel = document.createElement('div');
    xpLabel.style.cssText = `font-size: 9px; color: #4488cc; min-width: 20px;`;
    xpLabel.textContent = 'XP';

    const xpBarBg = document.createElement('div');
    xpBarBg.style.cssText = `
      width: 80px; height: 4px;
      background: rgba(50, 50, 50, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.06);
    `;

    const xpBarFill = document.createElement('div');
    xpBarFill.style.cssText = `
      width: 0%; height: 100%;
      background: #4488cc;
      transition: width 0.3s ease-out;
    `;
    this.elements.xpBarFill = xpBarFill;

    const levelText = document.createElement('div');
    levelText.style.cssText = `font-size: 8px; color: #aaaaaa; min-width: 40px; text-align: right;`;
    levelText.textContent = 'Lv.1';
    this.elements.levelText = levelText;

    xpBarBg.appendChild(xpBarFill);
    xpRow.appendChild(xpLabel);
    xpRow.appendChild(xpBarBg);
    xpRow.appendChild(levelText);
    bottomLeft.appendChild(xpRow);

    // Status effects row
    const statusRow = document.createElement('div');
    statusRow.style.cssText = `
      display: flex;
      gap: 3px;
      align-items: center;
      min-height: 8px;
      margin-left: 24px;
    `;
    this.elements.statusRow = statusRow;
    bottomLeft.appendChild(statusRow);

    // Gold counter
    const goldRow = document.createElement('div');
    goldRow.style.cssText = `display: flex; align-items: center; gap: 4px; margin-top: 1px;`;

    const goldIcon = document.createElement('span');
    goldIcon.style.cssText = `
      display: inline-block;
      width: 7px; height: 7px;
      background: #ffcc00;
      flex-shrink: 0;
    `;

    const goldText = document.createElement('span');
    goldText.style.cssText = `font-size: 9px; color: #ffcc00;`;
    goldText.textContent = '0';
    this.elements.goldText = goldText;

    goldRow.appendChild(goldIcon);
    goldRow.appendChild(goldText);
    bottomLeft.appendChild(goldRow);

    // Relics display (M21)
    const relicsRow = document.createElement('div');
    relicsRow.style.cssText = `
      display: flex;
      gap: 2px;
      align-items: center;
      margin-top: 1px;
    `;
    this.elements.relicsRow = relicsRow;
    bottomLeft.appendChild(relicsRow);

    this.elements.bottomLeft = bottomLeft;

    // === BOTTOM-RIGHT: Ability + Weapon ===
    const bottomRight = document.createElement('div');
    bottomRight.id = 'hud-bottom-right';
    bottomRight.style.cssText = `
      position: absolute;
      bottom: 4px; right: 4px;
      background: rgba(0, 0, 0, 0.55);
      border: 1px solid rgba(255, 255, 255, 0.08);
      padding: 4px 6px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
    `;

    // Ability display
    const abilityRow = document.createElement('div');
    abilityRow.style.cssText = `display: flex; align-items: center; gap: 4px;`;

    const abilityKeyLabel = document.createElement('span');
    abilityKeyLabel.style.cssText = `
      font-size: 9px; color: #888888;
      background: rgba(60, 60, 80, 0.6);
      padding: 1px 3px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    `;
    abilityKeyLabel.textContent = 'Q';
    this.elements.abilityKeyLabel = abilityKeyLabel;

    const abilityNameLabel = document.createElement('span');
    abilityNameLabel.style.cssText = `font-size: 9px; color: #cccccc;`;
    this.elements.abilityNameLabel = abilityNameLabel;

    const abilityCooldownLabel = document.createElement('span');
    abilityCooldownLabel.style.cssText = `font-size: 8px; color: #44ff44;`;
    abilityCooldownLabel.textContent = 'READY';
    this.elements.abilityCooldownLabel = abilityCooldownLabel;

    abilityRow.appendChild(abilityKeyLabel);
    abilityRow.appendChild(abilityNameLabel);
    abilityRow.appendChild(abilityCooldownLabel);
    bottomRight.appendChild(abilityRow);

    // Weapon name
    const weaponLabel = document.createElement('div');
    weaponLabel.style.cssText = `font-size: 9px; color: #88aacc;`;
    weaponLabel.textContent = 'No Weapon';
    this.elements.weaponLabel = weaponLabel;
    bottomRight.appendChild(weaponLabel);

    this.elements.bottomRight = bottomRight;

    // === BOTTOM-CENTER: Inventory Bar (1-5) ===
    const inventoryBar = document.createElement('div');
    inventoryBar.id = 'inventory-bar';
    inventoryBar.style.cssText = `
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 3px;
      pointer-events: none;
      font-family: 'Courier New', monospace;
    `;

    this.inventorySlots = [];
    for (let i = 0; i < 5; i++) {
      const slotWrapper = document.createElement('div');
      slotWrapper.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1px;
      `;

      const slot = document.createElement('div');
      slot.style.cssText = `
        width: 16px; height: 16px;
        background: rgba(60, 60, 80, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.1);
      `;

      const label = document.createElement('div');
      label.style.cssText = `font-size: 7px; color: #666666; text-align: center;`;
      label.textContent = (i + 1).toString();

      slotWrapper.appendChild(slot);
      slotWrapper.appendChild(label);
      inventoryBar.appendChild(slotWrapper);
      this.inventorySlots.push(slot);
    }

    this.elements.inventoryBar = inventoryBar;

    // === DAMAGE FLASH OVERLAY ===
    const damageFlash = document.createElement('div');
    damageFlash.style.cssText = `
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(200, 0, 0, 0.3);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.05s ease-in;
    `;
    this.elements.damageFlash = damageFlash;

    // === FLOOR TRANSITION OVERLAY ===
    const floorTransition = document.createElement('div');
    floorTransition.style.cssText = `
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: #000000;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
      z-index: 100;
    `;
    this.elements.floorTransition = floorTransition;

    const floorAnnounce = document.createElement('div');
    floorAnnounce.style.cssText = `
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      font-family: 'Courier New', monospace;
      font-size: 24px;
      font-weight: bold;
      color: #ffffff;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
      z-index: 101;
      text-shadow: 0 0 10px rgba(68, 204, 68, 0.8);
    `;
    this.elements.floorAnnounce = floorAnnounce;

    // Append all to UI root
    this.uiRoot.appendChild(topLeft);
    this.uiRoot.appendChild(topRight);
    this.uiRoot.appendChild(bossBarWrapper);
    this.uiRoot.appendChild(bottomLeft);
    this.uiRoot.appendChild(bottomRight);
    this.uiRoot.appendChild(inventoryBar);
    this.uiRoot.appendChild(damageFlash);
    this.uiRoot.appendChild(floorTransition);
    this.uiRoot.appendChild(floorAnnounce);
  }

  /**
   * Trigger a brief red damage flash overlay.
   */
  flashDamage() {
    const flash = this.elements.damageFlash;
    if (!flash) return;

    flash.style.transition = 'none';
    flash.style.opacity = '1';

    requestAnimationFrame(() => {
      flash.style.transition = 'opacity 0.2s ease-out';
      flash.style.opacity = '0';
    });
  }

  /**
   * Show floor transition effect: fade to black, show floor text, fade back.
   * @param {number} floorNumber - The floor number to display
   * @param {Function} onMidpoint - Callback fired at the midpoint (when screen is black)
   */
  showFloorTransition(floorNumber, onMidpoint) {
    const overlay = this.elements.floorTransition;
    const announce = this.elements.floorAnnounce;
    if (!overlay || !announce) return;

    announce.textContent = `Floor ${floorNumber}`;

    overlay.style.opacity = '1';
    announce.style.opacity = '1';

    setTimeout(() => {
      if (onMidpoint) onMidpoint();

      setTimeout(() => {
        overlay.style.opacity = '0';
        announce.style.opacity = '0';
      }, 400);
    }, 300);
  }

  /**
   * Update HUD display from GameState and scene data.
   * @param {object} gameState - The GameState singleton
   * @param {object} [sceneData] - Optional scene data
   */
  update(gameState, sceneData) {
    // Health
    const hp = gameState.playerHP;
    const maxHp = gameState.playerMaxHP;
    this.elements.hpText.textContent = `${hp}/${maxHp}`;
    const pct = maxHp > 0 ? (hp / maxHp) * 100 : 0;
    this.elements.hpBarFill.style.width = pct + '%';

    if (pct <= 25) {
      this.elements.hpBarFill.style.background = '#ff2222';
    } else if (pct <= 50) {
      this.elements.hpBarFill.style.background = '#cc6633';
    } else {
      this.elements.hpBarFill.style.background = '#cc3333';
    }

    // Floor
    this.elements.floorLabel.textContent = `Floor ${gameState.floor}`;

    // Class icon color
    if (sceneData && sceneData.classColor) {
      const hexColor = '#' + parseInt(sceneData.classColor, 16).toString(16).padStart(6, '0');
      this.elements.classIcon.style.background = hexColor;
    }

    // Room progress
    if (sceneData && sceneData.totalRooms !== undefined) {
      this.elements.roomLabel.textContent = `Rooms: ${sceneData.roomsCleared}/${sceneData.totalRooms}`;
    }

    // Gold
    this.elements.goldText.textContent = gameState.gold;

    // Crypt Tokens
    this.elements.tokenText.textContent = gameState.cryptTokens;

    // Weapon name
    if (sceneData && sceneData.weaponName) {
      this.elements.weaponLabel.textContent = sceneData.weaponName;
    } else {
      this.elements.weaponLabel.textContent = 'No Weapon';
    }

    // Ability indicator
    if (sceneData && sceneData.abilityName) {
      this.elements.abilityNameLabel.textContent = sceneData.abilityName;
      if (sceneData.abilityReady) {
        this.elements.abilityCooldownLabel.textContent = 'READY';
        this.elements.abilityCooldownLabel.style.color = '#44ff44';
        this.elements.abilityKeyLabel.style.borderColor = 'rgba(68, 255, 68, 0.3)';
      } else {
        const cd = Math.ceil(sceneData.abilityCooldown * 10) / 10;
        this.elements.abilityCooldownLabel.textContent = cd.toFixed(1) + 's';
        this.elements.abilityCooldownLabel.style.color = '#ff8844';
        this.elements.abilityKeyLabel.style.borderColor = 'rgba(255, 136, 68, 0.3)';
      }
    }

    // XP bar (M22)
    if (sceneData && sceneData.xpToNext !== undefined) {
      const xpPct = sceneData.xpToNext > 0 ? (sceneData.xp / sceneData.xpToNext) * 100 : 0;
      this.elements.xpBarFill.style.width = xpPct + '%';
      this.elements.levelText.textContent = `Lv.${sceneData.level || 1}`;
    }

    // Status effects
    if (sceneData && sceneData.statusEffects && this.elements.statusRow) {
      const row = this.elements.statusRow;
      row.innerHTML = '';
      for (const fx of sceneData.statusEffects) {
        const hexColor = '#' + (fx.color & 0xffffff).toString(16).padStart(6, '0');
        const remaining = Math.ceil(fx.remaining * 10) / 10;
        const stackText = fx.stacks > 1 ? ('x' + fx.stacks) : '';
        const dot = document.createElement('div');
        dot.style.cssText = `
          display: flex;
          align-items: center;
          gap: 1px;
          font-size: 7px;
          color: ${hexColor};
        `;
        const circle = document.createElement('span');
        circle.style.cssText = `
          display: inline-block;
          width: 5px; height: 5px;
          border-radius: 50%;
          background: ${hexColor};
          flex-shrink: 0;
        `;
        dot.appendChild(circle);
        dot.appendChild(document.createTextNode(remaining.toFixed(1) + 's' + stackText));
        row.appendChild(dot);
      }
    } else if (this.elements.statusRow) {
      this.elements.statusRow.innerHTML = '';
    }

    // Shield bar (Voidpriest)
    if (sceneData && sceneData.classId === 'voidpriest') {
      this.elements.shieldBarBg.style.display = 'block';
      const shieldPct = sceneData.shield > 0 ? Math.min(100, (sceneData.shield / 40) * 100) : 0;
      this.elements.shieldBarFill.style.width = shieldPct + '%';
    } else {
      this.elements.shieldBarBg.style.display = 'none';
    }

    // Boss health bar
    if (sceneData && sceneData.bossAlive && sceneData.bossHp !== undefined) {
      this.elements.bossBarWrapper.style.display = 'flex';
      this.elements.bossNameLabel.textContent = sceneData.bossName || 'Boss';
      const bossPct = sceneData.bossMaxHp > 0 ? (sceneData.bossHp / sceneData.bossMaxHp) * 100 : 0;
      this.elements.bossHpBarFill.style.width = bossPct + '%';
      this.elements.bossHpPctLabel.textContent = Math.ceil(bossPct) + '%';

      if (bossPct <= 25) {
        this.elements.bossHpBarFill.style.background = '#ff2222';
      } else if (bossPct <= 50) {
        this.elements.bossHpBarFill.style.background = '#cc6622';
      } else {
        this.elements.bossHpBarFill.style.background = '#cc4422';
      }
    } else {
      this.elements.bossBarWrapper.style.display = 'none';
    }

    // Relics display (M21)
    if (this.elements.relicsRow) {
      const row = this.elements.relicsRow;
      row.innerHTML = '';
      const relics = (sceneData && sceneData.relics) ? sceneData.relics : [];
      for (const relic of relics) {
        const color = relic.relicData.color || '0x888888';
        const hexColor = '#' + parseInt(color, 16).toString(16).padStart(6, '0');
        const dot = document.createElement('div');
        dot.title = relic.relicData.name + ': ' + relic.relicData.description;
        dot.style.cssText = `
          width: 8px; height: 8px;
          background: ${hexColor};
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
        row.appendChild(dot);
      }
    }

    // Inventory quick-slots
    const inventory = (sceneData && sceneData.inventory) ? sceneData.inventory : [];
    for (let i = 0; i < 5; i++) {
      const slot = this.inventorySlots[i];
      if (!slot) continue;
      if (i < inventory.length && inventory[i]) {
        const item = inventory[i];
        const color = item.itemData.color || '0x888888';
        const hexColor = '#' + parseInt(color, 16).toString(16).padStart(6, '0');
        slot.style.background = hexColor;
        slot.style.border = '1px solid rgba(255, 255, 255, 0.3)';
        const type = item.itemData.type;
        if (type === 'weapon') slot.style.borderColor = 'rgba(136, 204, 255, 0.5)';
        else if (type === 'armor') slot.style.borderColor = 'rgba(139, 105, 20, 0.5)';
        else if (type === 'accessory') slot.style.borderColor = 'rgba(204, 136, 68, 0.5)';
        else if (type === 'consumable') slot.style.borderColor = 'rgba(255, 68, 68, 0.5)';
      } else {
        slot.style.background = 'rgba(60, 60, 80, 0.5)';
        slot.style.border = '1px solid rgba(255, 255, 255, 0.1)';
      }
    }
  }

  /**
   * Remove HUD elements from the DOM.
   */
  destroy() {
    const elementsToRemove = [
      'topLeft', 'topRight', 'bossBarWrapper', 'bottomLeft',
      'bottomRight', 'inventoryBar', 'damageFlash', 'floorTransition', 'floorAnnounce'
    ];
    for (const key of elementsToRemove) {
      const el = this.elements[key];
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    }
    this.elements = {};
    this.inventorySlots = [];
  }
}
